/**
 * Data Formatting Utilities for UAE Standards
 * 
 * 1. Mobile Number: UAE format (+971XXXXXXXXX)
 * 2. Emirates ID: 15 digits without dashes
 * 3. Name: Title Case (First letter of each word capitalized)
 */

/**
 * Format UAE Mobile Number
 * Converts various formats to standard +971XXXXXXXXX format
 * @param {string} mobile - Input mobile number
 * @returns {string} Formatted mobile number
 */
export const formatUAEMobile = (mobile) => {
  if (!mobile) return '';
  
  // Remove all non-digit characters except +
  let cleaned = mobile.replace(/[^\d+]/g, '');
  
  // Handle different input formats
  if (cleaned.startsWith('+971')) {
    // Already in correct format, just ensure 9 digits after +971
    const digits = cleaned.substring(4);
    return '+971' + digits.substring(0, 9);
  } else if (cleaned.startsWith('971')) {
    // Missing +
    const digits = cleaned.substring(3);
    return '+971' + digits.substring(0, 9);
  } else if (cleaned.startsWith('00971')) {
    // International format with 00
    const digits = cleaned.substring(5);
    return '+971' + digits.substring(0, 9);
  } else if (cleaned.startsWith('0')) {
    // Local format starting with 0
    const digits = cleaned.substring(1);
    return '+971' + digits.substring(0, 9);
  } else if (cleaned.startsWith('+')) {
    // Other country code, return as is
    return cleaned;
  } else {
    // Just digits, assume UAE local number
    return '+971' + cleaned.substring(0, 9);
  }
};

/**
 * Validate UAE Mobile Number
 * @param {string} mobile - Mobile number to validate
 * @returns {boolean} True if valid UAE mobile
 */
export const isValidUAEMobile = (mobile) => {
  if (!mobile) return false;
  const formatted = formatUAEMobile(mobile);
  // UAE mobile should be +971 followed by 9 digits
  // First digit after +971 should be 5 (mobile) or 4/2/3/6/7/9 (landline/other)
  return /^\+971[0-9]{9}$/.test(formatted);
};

/**
 * Display formatted UAE Mobile (with spaces for readability)
 * @param {string} mobile - Mobile number
 * @returns {string} Display formatted number (+971 XX XXX XXXX)
 */
export const displayUAEMobile = (mobile) => {
  const formatted = formatUAEMobile(mobile);
  if (!formatted || formatted.length < 13) return formatted;
  // Format as +971 XX XXX XXXX
  return formatted.substring(0, 4) + ' ' + 
         formatted.substring(4, 6) + ' ' + 
         formatted.substring(6, 9) + ' ' + 
         formatted.substring(9);
};

/**
 * Format Emirates ID - Remove all dashes and non-digits, keep only 15 digits
 * @param {string} emiratesId - Input Emirates ID
 * @returns {string} 15-digit Emirates ID without dashes
 */
export const formatEmiratesId = (emiratesId) => {
  if (!emiratesId) return '';
  
  // Remove all non-digit characters
  const digitsOnly = emiratesId.replace(/\D/g, '');
  
  // Return first 15 digits
  return digitsOnly.substring(0, 15);
};

/**
 * Validate Emirates ID
 * @param {string} emiratesId - Emirates ID to validate
 * @returns {boolean} True if valid (exactly 15 digits)
 */
export const isValidEmiratesId = (emiratesId) => {
  if (!emiratesId) return false;
  const formatted = formatEmiratesId(emiratesId);
  return formatted.length === 15;
};

/**
 * Display formatted Emirates ID (with dashes for readability)
 * @param {string} emiratesId - Emirates ID
 * @returns {string} Display formatted (XXX-XXXX-XXXXXXX-X)
 */
export const displayEmiratesId = (emiratesId) => {
  const formatted = formatEmiratesId(emiratesId);
  if (!formatted || formatted.length !== 15) return formatted;
  // Format as XXX-XXXX-XXXXXXX-X
  return formatted.substring(0, 3) + '-' + 
         formatted.substring(3, 7) + '-' + 
         formatted.substring(7, 14) + '-' + 
         formatted.substring(14);
};

/**
 * Format Name to Title Case
 * Each word starts with capital letter, rest lowercase
 * @param {string} name - Input name
 * @returns {string} Title case formatted name
 */
export const formatName = (name) => {
  if (!name) return '';
  
  return name
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .trim();
};

/**
 * Format Name to UPPERCASE
 * @param {string} name - Input name
 * @returns {string} Uppercase name
 */
export const formatNameUppercase = (name) => {
  if (!name) return '';
  return name.toUpperCase().trim();
};

/**
 * Standardize all rider/user data fields
 * @param {object} data - Object with name, emirates_id, mobile fields
 * @returns {object} Standardized data object
 */
export const standardizeUserData = (data) => {
  const standardized = { ...data };
  
  if (data.name) {
    standardized.name = formatName(data.name);
  }
  
  if (data.emirates_id) {
    standardized.emirates_id = formatEmiratesId(data.emirates_id);
  }
  
  if (data.mobile_uae) {
    standardized.mobile_uae = formatUAEMobile(data.mobile_uae);
  }
  
  if (data.mobile) {
    standardized.mobile = formatUAEMobile(data.mobile);
  }
  
  if (data.phone) {
    standardized.phone = formatUAEMobile(data.phone);
  }
  
  return standardized;
};

/**
 * Input handler for Emirates ID (auto-format as user types)
 * @param {string} value - Current input value
 * @returns {string} Formatted value (digits only, max 15)
 */
export const handleEmiratesIdInput = (value) => {
  // Allow only digits, max 15 characters
  return value.replace(/\D/g, '').substring(0, 15);
};

/**
 * Input handler for UAE Mobile (auto-format as user types)
 * @param {string} value - Current input value
 * @returns {string} Formatted value
 */
export const handleMobileInput = (value) => {
  // Allow +, digits, and spaces
  let cleaned = value.replace(/[^\d+\s]/g, '');
  
  // If starts with 0, convert to +971
  if (cleaned.startsWith('0') && !cleaned.startsWith('00')) {
    cleaned = '+971' + cleaned.substring(1);
  }
  
  // If doesn't start with +, add +971
  if (cleaned && !cleaned.startsWith('+') && !cleaned.startsWith('0')) {
    if (cleaned.length <= 9) {
      cleaned = '+971' + cleaned;
    }
  }
  
  return cleaned;
};

export default {
  formatUAEMobile,
  isValidUAEMobile,
  displayUAEMobile,
  formatEmiratesId,
  isValidEmiratesId,
  displayEmiratesId,
  formatName,
  formatNameUppercase,
  standardizeUserData,
  handleEmiratesIdInput,
  handleMobileInput
};
